/** @jsxImportSource @emotion/react */
import React from "react";
import { css } from "@emotion/react";
import { colors } from "../../colors";
import { RefCallBack } from "react-hook-form";
import { FieldSkeleton } from "./FieldSkeleton";

interface Props
  extends React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  label?: string;
  errorMessage?: string;
  innerRef?: RefCallBack | React.RefObject<HTMLInputElement>;
  helperText?: string;
  labelIcon?: React.ReactNode;
  floatingElement?: React.ReactNode;
}

export const floatingElementStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  right: 8px;
  width: fit-content;
  height: 24px;
  min-height: 24px;
  min-width: 24px;
  border-radius: 4px;
  transform: translateY(-50%);
  top: 50%;
  margin: auto;
`;

export const inputContainerStyle = css`
  position: relative;
`;

export const additionalStyling = css`
  min-height: 38px;
`;

export const returnInputStyle = (errorMessage: string | undefined) => {
  return css`
    border-radius: 3px;
    font-size: 14px;
    padding: 7.5px 12px;
    min-width: max-content;
    width: 100% !important;
    font-family: Inter;
    -webkit-appearance: none !important;
    border: 1px solid ${errorMessage ? colors.errorMedium : colors.greyBorder};
    &:disabled {
      background: ${colors.whiteHover};
      $:hover {
        background: ${colors.whiteHover};
      }
    }
    &:focus {
      // Fixing colors similar to what we have now, and then we can change UI once all are changed
      outline: ${errorMessage
          ? "rgba(220, 20, 45, 0.2)"
          : "rgba(70, 127, 207, 0.2)"}
        solid 2px;
      border-color: ${errorMessage
        ? colors.errorMedium
        : "rgba(70, 127, 207, 1)"};
    }
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  `;
};

export const TextField = ({
  label,
  errorMessage,
  innerRef,
  helperText,
  labelIcon,
  type,
  floatingElement,
  ...rest
}: Props) => {
  return (
    <FieldSkeleton
      errorMessage={errorMessage}
      helperText={helperText}
      label={label}
      labelIcon={labelIcon}
    >
      <div css={inputContainerStyle}>
        <input
          css={[returnInputStyle(errorMessage), additionalStyling]}
          type={type || "text"}
          autoComplete="off"
          ref={innerRef}
          {...rest}
        />
        {floatingElement && (
          <div css={floatingElementStyle}>{floatingElement}</div>
        )}
      </div>
    </FieldSkeleton>
  );
};
